import "./LoginQuestion.css";
import RadioButton from "../RadioButton/RadioButton";
import { useRef, useState } from "react";
import SubmitButton from "../../../../components/SubmitButton/SubmitButton";
import InputField from "../../../../components/CredentialsInput/InputField";
import * as http from "../../../../utils/http";
import * as translator from "../../../../utils/translator";
import StickyNotification from "../../../../components/StickyNotification/StickyNotification";
import { MainTracker } from "../../../../PixelTrackers/MainTracker";
import useForm from "../../../../customHooks/useForm";
import Significance from "../Significance/Significance";
import { useCustomer } from "../../../../customHooks/useCustomer";
import SocialLoginButtons from "../../../../components/LoginStrategies/SocialLoginButtons";
import { useCartContext } from "../../../../customHooks/useCart";
import { clientConfig } from "../../../../config";
import ReCAPTCHA from "react-google-recaptcha";

export default function LoginQuestion({
  onChange,
  onFirstTimeAnswerChange,
  language,
  country,
  showForgotPasswordModal,
  setCustomerZipcode,
  OAuthRedirectPath,
  OAuthCameFromPath,
  showLoginWindowParam = false,
  shouldScrollOnAnswer = false,
}) {
  const [showLoginWindow, setShowLoginWindow] = useState(showLoginWindowParam);
  const { updateCart, getCart } = useCartContext();
  const reRef = useRef(null);
  const [stickyNotification, setStickyNotification] = useState({ show: false, errorCode: 'login_failed' });
  const [submitButtonLoading, setSubmitButtonLoading] = useState(false);
  const { saveCustomer, customerState } = useCustomer();
  const loginFormAttributes = useForm(
    { email: "", loginPassword: "", country },
    language,
    undefined,
    { looseEmail: true }
  );

  const onSubmit = async (e) => {
    e.preventDefault();

    setSubmitButtonLoading(true);

    const cart = getCart();
    const questionnaireShouldIDVerify = cart.should_id_verify;
    
    if (loginFormAttributes.validateForm()) {

      let captchaToken

      if (clientConfig.IS_RECAPTCHA_ENABLED) {
        captchaToken = await reRef.current?.executeAsync();
        reRef.current?.reset();
      }

      const requestBody = {...loginFormAttributes.form, questionnaireShouldIDVerify, captchaToken };

      await http
        .postToServer(`auth/local/login`, requestBody, [http.MIDDLEWARES.CART_ORDER, http.MIDDLEWARES.ENCRYPTED_BODY])
        .then((response) => {
          if (response.data.status === "ok") {
            saveCustomer({
              type: "logged_in",
              customerID: response.data.customerID,
              uuid: response.data.uuid,
              token: response.data.token,
              email: loginFormAttributes.form.email,
              optForSendables: response.data.optForSendables,
              loginSource: "questionnaire",
              fullName: response.data.customerFullName,
              provider: response.data.provider || 'local',
              gender: response.data?.gender,
              lastPaidCategoryMention: response.data?.lastPaidCategoryMention,
            });
            const customerZipCode = response.data.customerZipCode;
            if (customerZipCode) {
              setCustomerZipcode(customerZipCode);
            }

            updateCart({
              should_id_verify: response.data.shouldIDVerifyForNewCart
            })
            
          } else {
            setStickyNotification(prevStickyNotification => ({ ...prevStickyNotification, show: true }));
          }
        })
        .catch((error) => {
          let errorCode = 'login_failed'
          if (error?.response?.data?.status === 'user_blocked') {
            errorCode = 'user_blocked'
          }
          setStickyNotification(prevStickyNotification => ({ show:true, errorCode }))
        });
    }
    setSubmitButtonLoading(false);
  };


  if (customerState) {
    //if customer is Logged-in, we don't show the question
    return null;
  } else {
    return (
      <>
        {stickyNotification.show ? (
          <StickyNotification
            showNotification={stickyNotification.show}
            title={translator.translate(
              language,
              "default",
              "StickyNotification/danger_title"
            )}
            onClick={() => setStickyNotification(prevStickyNotification => ({ ...prevStickyNotification, show: false }))}
            content={translator.translate(
              language,
              "default",
              `StickyNotification/${stickyNotification.errorCode}`
            )}
          />
        ) : null}

        <div className="questionnaire-question-item-container">
          <div className="questionnaire-question-item">
            <div className="questionnaire-question-item-text-container">
              {translator.translate(
                language,
                "default",
                "LoginQuestion/first_time_ordering"
              )}
            </div>
            <div className="questionnaire-answers-container">
              <RadioButton
                value={"No"}
                id={"login_no"}
                groupName={"LoginQuestion"}
                label={translator.translate(
                  language,
                  "default",
                  "LoginQuestion/no"
                )}
                onClick={() => {
                  MainTracker.track("custom", "View Login Box");
                  setShowLoginWindow(true);
                  onChange(true);
                  onFirstTimeAnswerChange?.(false);
                  if (shouldScrollOnAnswer) {
                    setTimeout(() => {
                      document.querySelector(".questionnaire-subquestion-container")?.scrollIntoView({ behavior: "smooth" });
                    }, 150);
                  }
                }}
                selected={showLoginWindow}

              />
              <RadioButton
                value={"Yes"}
                id={"login_yes"}
                groupName={"LoginQuestion"}
                label={translator.translate(
                  language,
                  "default",
                  "LoginQuestion/yes"
                )}
                onClick={() => {
                  setShowLoginWindow(false);
                  onFirstTimeAnswerChange?.(true);
                  if (shouldScrollOnAnswer) {
                    setTimeout(() => {
                      document.getElementById("questionnaire-form")?.scrollIntoView({ behavior: "smooth" });
                    }, 150);
                  }
                }}
              />
            </div>
          </div>
        </div>

        {showLoginWindow ? (
          <div className="questionnaire-subquestion-container">
            <p className="questionnaire-loginquestion-title">
              {translator.translate(
                language,
                "default",
                "LoginQuestion/login_and_continue"
              )}
            </p>

            <SocialLoginButtons
              redirectPath={OAuthRedirectPath}
              cameFromPath={OAuthCameFromPath}
              language={language}
              country={country}
              onCompleteSignup={() => {}}
              onFailureSignup={() => {     // Show sticky notification error
                setStickyNotification({
                  show: true,
                  title: translator.translate(language, "default", "StickyNotification/danger_title"),
                  content: translator.translate(language, "default", "StickyNotification/oauth_failed"),
                  level: "error", 
                  showInfoIcon: true,
                  refresh: false
                });
               }}
            />

            {/* Divider */}
            <div className="login-modal-form-container-or">
              <div className="login-modal-form-container-or-line" />
              <span className="login-modal-form-container-or-text">OR</span>
              <div className="login-modal-form-container-or-line" />
            </div>

            <form onSubmit={onSubmit} className="questionnaire-loginquestion-inputs-container" id="questionnaire-login-form">
              <InputField
                value={loginFormAttributes.form.email}
                errorMsg={loginFormAttributes.errorMessagePerField.email}
                onChange={(value) => {
                  loginFormAttributes.updateForm("email", value);
                }}
                LoginQuestion
                type="email"
                placeholder={"E-Mail"}
                language={language}
                mandatory
                onBlur={() => loginFormAttributes.onFieldBlur("email")}
                touched={loginFormAttributes.touched.email}
              />

              <InputField
                value={loginFormAttributes.form.loginPassword}
                errorMsg={
                  loginFormAttributes.errorMessagePerField.loginPassword
                }
                onChange={(value) => {
                  loginFormAttributes.updateForm("loginPassword", value);
                }}
                type="password"
                placeholder={translator.translate(
                  language,
                  "default",
                  "LoginQuestion/password"
                )}
                language={language}
                mandatory
                onBlur={() => loginFormAttributes.onFieldBlur("loginPassword")}
                touched={loginFormAttributes.touched.loginPassword}
              />
              <input type="submit" hidden />
              {clientConfig.IS_RECAPTCHA_ENABLED &&
              <ReCAPTCHA
                sitekey={clientConfig.RECAPTCHA_SITE_KEY}
                size="invisible"
                ref={reRef}
              />
              }
            </form>

            <SubmitButton
              id="login-question-submit"
              language={language}
              loading={submitButtonLoading}
              onSubmit={onSubmit}
              width={"100%"}
              placeholder={translator.translate(
                language,
                "default",
                "LoginQuestion/login_and_continue"
              )}
            />

            <p
              onClick={() => showForgotPasswordModal()}
              className="loginquestion-forgot-password"
            >
              {translator.translate(
                language,
                "default",
                "LoginQuestion/forgot_password"
              )}
            </p>
          </div>
        ) : null}
        <Significance
          level={'info'}
          text={translator.translate(language, "default", "skip_questionnaire/disclaimer2")}
          fontWeight={'500'}
          fontSize={'13px'}
          margin={'0 0 25px 0'}
          dangerSignificancesRefsDispatch={null}
          parentID={null}
        />
      </>
    );
  }
}
